import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Td,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import FieldReportingSource from 'api/field_reporting';
import { strings } from 'config/localization';
import PermissionRequest from 'constants/PermissionRequest';
import routes from 'constants/routes';
import { FieldReportingSchema } from 'constants/schema';
import React, { useState } from 'react';
import { BiShow, BiTrash } from 'react-icons/bi';
import { useMutation, useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import AccessControl from 'services/AccessControl';
import { truncateString } from 'utils';
import { getFullName } from 'utils/esg';

interface Props {
  fieldReportData: FieldReportingSchema;
  index: number;
}
const FieldReportingListItem: React.FC<Props> = (props) => {
  const { fieldReportData, index } = props;
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const fieldReportingAPI = new FieldReportingSource();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [deleteMsg, setDeleteMsg] = useState('');

  const deleteFieldReporting = useMutation(
    () => fieldReportingAPI.destroy(fieldReportData.id),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('fieldReportList');

        if (res.data[0].success) {
          setDeleteModalOpen(false);
          toast({
            title: strings.field_report_delete_success_msg,
            status: 'success',
            isClosable: true,
          });
        }
      },
    }
  );

  const onDeletePress = () => {
    setDeleteMsg('');
    setDeleteModalOpen(true);
  };

  const onDeleteModalClose = () => {
    setDeleteMsg('');
    setDeleteModalOpen(false);
  };

  const onDeleteConfirm = () => {
    deleteFieldReporting.mutate();
  };

  const stringifyFields = (fields: any) => {
    return Array.prototype.map
      .call(fields, function (item) {
        return item.measurement;
      })
      .join(', ');
  };

  return (
    <Tr key={fieldReportData.id}>
      <Td>{index}</Td>
      <Td>
        <RouterLink
          to={routes.fieldReporting.environmentTracker.view.replace(
            ':id',
            fieldReportData?.id.toString()
          )}>
          {fieldReportData?.name || '-'}
        </RouterLink>
      </Td>
      <Td>{truncateString(fieldReportData?.description) || '-'}</Td>
      <Td>
        {truncateString(stringifyFields(fieldReportData?.field_report_unit))}
      </Td>
      <Td>{getFullName(fieldReportData?.created_by) || '-'}</Td>
      <Td>{fieldReportData?.company_detail?.name || '-'}</Td>
      <Td>
        <RouterLink
          to={routes.fieldReporting.environmentTracker.view.replace(
            ':id',
            fieldReportData.id.toString()
          )}>
          <Tooltip hasArrow label={strings.view_field_report}>
            <IconButton
              icon={<BiShow />}
              variant="link"
              aria-label={strings.view_field_report}
              color="green.300"
              size="lg"
            />
          </Tooltip>
        </RouterLink>

        <AccessControl
          allowedPermissions={[
            PermissionRequest['read:field-report'],
            PermissionRequest['manage:field-report'],
          ]}
          renderNoAccess={(data: any) => data}>
          <Tooltip hasArrow label={strings.delete_field_report}>
            <IconButton
              icon={<BiTrash />}
              variant="link"
              aria-label={strings.delete_field_report}
              color="red.300"
              size="lg"
              onClick={onDeletePress}
            />
          </Tooltip>
        </AccessControl>
        <Modal
          isOpen={isDeleteModalOpen}
          isCentered
          onClose={onDeleteModalClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{strings.delete_field_report}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {strings.confirm_delete}
              {deleteMsg && (
                <Alert status="error" mb={2}>
                  <AlertIcon />
                  {deleteMsg}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  colorScheme="red"
                  onClick={onDeleteConfirm}
                  isLoading={deleteFieldReporting.isLoading}>
                  {strings.delete}
                </Button>

                <Button variant="outline" onClick={onDeleteModalClose}>
                  {strings.cancel}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Td>
    </Tr>
  );
};

export default FieldReportingListItem;
